import {cva, type VariantProps} from 'class-variance-authority'
import {Tooltip as TooltipPrimitive} from '@base-ui/react/tooltip'
import {cn} from '@utilities/helpers'
import {PropsWithChildren, ReactNode, useState} from 'react'

const tooltipContentVariants = cva(
    'z-tooltip flex w-fit max-w-[90vw] origin-(--transform-origin) flex-col items-start gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold wrap-break-word shadow-lg has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
    {
        variants: {
            variant: {
                dark: 'bg-neutral-900 text-white',
                light: 'bg-white text-neutral-700'
            }
        },
        defaultVariants: {
            variant: 'dark'
        }
    }
)

const tooltipArrowVariants = cva(
    'z-tooltip size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5',
    {
        variants: {
            variant: {
                dark: 'bg-neutral-900 fill-neutral-900',
                light: 'bg-white fill-white'
            }
        },
        defaultVariants: {
            variant: 'dark'
        }
    }
)

function TooltipProvider({delay = 0, ...props}: TooltipPrimitive.Provider.Props) {
    return <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />
}

function TooltipRoot({...props}: TooltipPrimitive.Root.Props) {
    return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({...props}: TooltipPrimitive.Trigger.Props) {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
    className,
    side = 'top',
    sideOffset = 4,
    align = 'center',
    alignOffset = 0,
    variant = 'dark',
    children,
    ...props
}: TooltipPrimitive.Popup.Props &
    Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'> &
    VariantProps<typeof tooltipContentVariants>) {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Positioner
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
                className="isolate z-50"
            >
                <TooltipPrimitive.Popup
                    data-slot="tooltip-content"
                    className={cn(tooltipContentVariants({variant}), className)}
                    {...props}
                >
                    {children}
                    <TooltipPrimitive.Arrow className={cn(tooltipArrowVariants({variant}))} />
                </TooltipPrimitive.Popup>
            </TooltipPrimitive.Positioner>
        </TooltipPrimitive.Portal>
    )
}

interface TooltipProps extends PropsWithChildren {
    content: ReactNode
    contentProps?: TooltipPrimitive.Popup.Props &
        Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'> &
        VariantProps<typeof tooltipContentVariants>
    delay?: number
    rootProps?: TooltipPrimitive.Root.Props
    triggerProps?: TooltipPrimitive.Trigger.Props
    variant?: 'light' | 'dark'
}

function Tooltip({
    children,
    rootProps,
    triggerProps,
    contentProps,
    content,
    variant = 'dark',
    delay = 350
}: TooltipProps) {
    const [open, setOpen] = useState(false)

    return (
        <TooltipProvider delay={delay}>
            <TooltipRoot open={open} onOpenChange={setOpen} {...rootProps}>
                <TooltipTrigger
                    {...triggerProps}
                    onClick={(...args) => {
                        setOpen(true)
                        triggerProps?.onClick?.(...args)
                    }}
                >
                    {children}
                </TooltipTrigger>

                <TooltipContent {...contentProps} variant={variant}>
                    {content}
                </TooltipContent>
            </TooltipRoot>
        </TooltipProvider>
    )
}

export {Tooltip, TooltipRoot, TooltipTrigger, TooltipContent, TooltipProvider}
